import {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { isEqualStr } from '@Helpers';
import { CustomData, useData } from '@kameleoon/react-sdk';
import { Button, RadioButtonGroup } from '@selina-finance/ui';
import cookie from 'cookie';
import { Form, Formik } from 'formik';
import { pick } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import { AuthContext } from '@App/contexts/Auth';
import { EMPLOYMENT_STATUS_TEST_CUSTOM_DATA_INDEX } from '@Shared/constants';
import { handleAuthResult } from '@Shared/helpers/auth';
import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
    handleSubFormValidation,
} from '@Shared/helpers/input';
import { formItems as incomingsFormItems } from '@src/app/formModels/eligibility/incomings';
import { employmentStatusOptions } from '@src/app/formModels/eligibility/incomings';
import { employmentStatuses } from '@src/app/formModels/eligibility/incomings.shared';
import { CertifyDetailsCallout } from '@src/ui/components/CertifyDetailsCallout/CertifyDetailsCallout';
import { FormError } from '@src/ui/components/FormError';
import { HttpError } from '@src/ui/components/HttpError';

import ErrorContainer from '../../components/ErrorContainer/ErrorContainer';

import IncomingContractorView from './IncomingContractorView';
import IncomingEmployedView from './IncomingEmployedView';
import IncomingNotPaidView from './IncomingNotPaid';
import IncomingRetiredView from './IncomingRetiredView';
import IncomingSelfEmployedView from './IncomingSelfEmployedView';

export interface IncomingViewProps {
    formRef: any;
    saveRef: any;
    className?: string;
    secondary?: boolean;
    incomeData?: any;
    setIncomeData: Function;
    isPrimaryApplicant: boolean;
    setActiveApplicant: Function;
    applicantCount: number;
    saveIncomings: (
        data: any,
        isPrimaryApplicant: boolean,
        noOfApplicants?: number,
    ) => Promise<any>;
}

const defaultValues = getDefaultValues(incomingsFormItems);
const validationSchema = getValidationSchema(incomingsFormItems);

const IncomingView = forwardRef((props: IncomingViewProps) => {
    const { addData, flush } = useData();
    const [error, setError] = useState(null);
    const [authErrors, setAuthErrors] = useState({});

    const formikRef = useRef<any>(null);
    const subFormikRef = useRef<any>(null);
    const navigate = useNavigate();

    const {
        setCurrentForm,
        applicants,
        applicationEmail,
        deferredApplicant2TestUser,
        sentryLog,
        flushLogs,
    } = useContext(ApplicationContext);

    const { loggedIn, register } = useContext(AuthContext);

    const setSubFormRef = useCallback((ref: any) => {
        subFormikRef.current = ref;
    }, []);

    useEffect(() => {
        setCurrentForm(undefined);
    }, [setCurrentForm]);

    useEffect(() => {
        if (formikRef.current) {
            setCurrentForm(formikRef.current);
        }
    }, [formikRef, setCurrentForm]);

    const validateAndSubmit = async () => {
        setError(null);

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        await new Promise((resolve) => window.requestAnimationFrame(resolve));

        const values = formikRef.current.values;
        const isValid = await handleSubFormValidation(formikRef.current);
        const isSubFormValid = await handleSubFormValidation(
            subFormikRef.current || null,
            true,
            sentryLog,
        );

        if (!subFormikRef.current) {
            sentryLog('subFormikRef.current is null');
        }

        if (!isSubFormValid || !isValid) {
            flushLogs();
            return false; // there was some error in the forms
        }

        const subFormValues = await subFormikRef.current
            .submitForm()
            .then((data: any) => data);

        if (!subFormValues) {
            sentryLog('submitForm returned no values');
            flushLogs();
            return false; // there was some unhandled error in the subForms
        }

        const finalValues = {
            ...pick(values, 'employmentStatus'),
            ...subFormValues,
        };

        const employmentStatus =
            finalValues.employmentStatus === employmentStatuses.selfEmployed
                ? `Self-employed: ${finalValues.selfEmployedType}`
                : finalValues.employmentStatus;

        try {
            const customData = new CustomData(
                EMPLOYMENT_STATUS_TEST_CUSTOM_DATA_INDEX,
                employmentStatus,
            );
            addData(applicationEmail, customData);

            flush(applicationEmail);
        } catch (e) {
            sentryLog(`Kameleoon addData failed: ${e}`);
        }

        const data = await props.saveIncomings(
            finalValues,
            props.isPrimaryApplicant,
            props.applicantCount,
        );

        const currentPath = window.location.pathname;

        if (
            deferredApplicant2TestUser &&
            currentPath === '/eligibility/income2'
        ) {
            setAuthErrors({});

            if (!data.success) {
                setError(data.error);
                return false;
            }

            if (loggedIn) {
                navigate('/eligibility/wait');
                return true;
            }

            const cookies = cookie.parse(document.cookie);
            const appInfo = cookies.application
                ? JSON.parse(cookies.application)
                : {};

            const result = await register(
                applicationEmail,
                appInfo.applicationId,
            );

            handleAuthResult(
                result,
                { email: applicationEmail },
                setAuthErrors,
                () => navigate('/eligibility'),
            );

            return true;
        }

        if (
            currentPath === '/eligibility/income' &&
            applicants.length === 2 &&
            !deferredApplicant2TestUser
        ) {
            navigate('/eligibility/income2');
        }

        if (
            data.success &&
            (!data.hasCoApplicant || deferredApplicant2TestUser)
        ) {
            navigate('/eligibility/outgoings');
            return true;
        }

        if (!data.success) {
            setError(data.error);
            return false;
        }

        return false;
    };

    const saveForLater = async () => {
        const values = formikRef.current.values;
        props.saveIncomings &&
            (await props
                .saveIncomings(
                    values,
                    props.isPrimaryApplicant,
                    props.applicantCount,
                )
                .then((data: any) => {
                    if (data.success && !data.hasCoApplicant) {
                        return true;
                    }
                    if (!data.success) {
                        setError(data.error);
                        return false;
                    }
                }));
    };

    useImperativeHandle(props.formRef, () => {
        return { validateAndSubmit };
    });

    useImperativeHandle(props.saveRef, () => ({
        saveMethod: saveForLater,
    }));

    return (
        <div className={props.className}>
            <Formik
                enableReinitialize
                initialValues={{
                    ...defaultValues,
                    ...pick(props.incomeData, Object.keys(incomingsFormItems)),
                }}
                validationSchema={validationSchema}
                onSubmit={validateAndSubmit}
                innerRef={(ref) => (formikRef.current = ref)}
            >
                {(formikProps) => {
                    const { getError } = getFormHelpers(
                        incomingsFormItems,
                        formikProps,
                    );

                    const employmentStatus =
                        formikProps.values.employmentStatus;
                    return (
                        <Form>
                            <div className='grid grid-cols-1 mt-6 mb-6 gap-10'>
                                <RadioButtonGroup
                                    name='employmentStatus'
                                    id='employmentStatus'
                                    value={
                                        formikProps.values.employmentStatus?.toString() ||
                                        ''
                                    }
                                    className='flex-col gap-2'
                                    error={getError('employmentStatus')}
                                    onChange={(e) => {
                                        if (
                                            e.currentTarget.value !==
                                            formikProps?.values
                                                ?.employmentStatus
                                        ) {
                                            props.setIncomeData({
                                                employmentStatus:
                                                    e.currentTarget.value,
                                            });
                                            formikProps.setFieldValue(
                                                'employmentStatus',
                                                e.currentTarget.value,
                                            );
                                        }
                                    }}
                                    options={employmentStatusOptions}
                                />
                                {isEqualStr(employmentStatus, 'employed') && (
                                    <IncomingEmployedView
                                        incomeData={props.incomeData}
                                        secondary={props.secondary}
                                        setRef={setSubFormRef}
                                        isPrimaryApplicant={
                                            props.isPrimaryApplicant
                                        }
                                    />
                                )}
                                {isEqualStr(
                                    employmentStatus,
                                    'self-employed',
                                ) && (
                                    <IncomingSelfEmployedView
                                        incomeData={props.incomeData}
                                        secondary={props.secondary}
                                        setRef={setSubFormRef}
                                    />
                                )}
                                {isEqualStr(employmentStatus, 'contractor') && (
                                    <IncomingContractorView
                                        incomeData={props.incomeData}
                                        secondary={props.secondary}
                                        setRef={setSubFormRef}
                                    />
                                )}
                                {isEqualStr(employmentStatus, 'retired') && (
                                    <IncomingRetiredView
                                        incomeData={props.incomeData}
                                        secondary={props.secondary}
                                        setRef={setSubFormRef}
                                    />
                                )}
                                {isEqualStr(
                                    employmentStatus,
                                    'Not in paid employment',
                                ) && (
                                    <IncomingNotPaidView
                                        incomeData={props.incomeData}
                                        secondary={props.secondary}
                                        setRef={setSubFormRef}
                                    />
                                )}
                            </div>
                            {error && <HttpError error={error} />}
                            <ErrorContainer formikProps={formikProps} />
                            <FormError otherErrors={authErrors} />

                            {deferredApplicant2TestUser &&
                                !props.isPrimaryApplicant && (
                                    <CertifyDetailsCallout />
                                )}
                            <div className='flex gap-5 justify-end mt-12'>
                                <Button
                                    type='submit'
                                    size='large'
                                    color='secondary'
                                    className='w-full'
                                >
                                    Continue
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
});

export default IncomingView;
